import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { Loader2, Eye, EyeOff } from 'lucide-react'

import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import useAuth from '../../utilities/hooks/useAuth'
import { extractErrorMessage } from '../../utilities/errorHelpers'

import leevoCover from '../../images/kids-sports.jpg'

const features = ['Streamlined class management', 'Real-time progress tracking', 'Parent and student engagement']

const stats = [
  { value: '500+', label: 'Programs' },
  { value: '50K+', label: 'Students' },
  { value: '99%', label: 'Satisfaction' }
]

function LoginPage({ notificationMessage }) {
  const { signInUser } = useAuth()
  const [loginError, setLoginError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const isMountedRef = useRef(true)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch
  } = useForm({
    defaultValues: {
      email: '',
      password: ''
    }
  })

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const subscription = watch(() => {
      if (loginError) {
        setLoginError('')
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, loginError])

  async function onSubmit(data) {
    setLoginError('')
    try {
      await signInUser({ email: data.email, password: data.password })
    } catch (error) {
      if (isMountedRef.current) {
        setLoginError(extractErrorMessage(error))
      }
    }
  }

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      <div
        className="relative hidden lg:block"
        style={{
          backgroundImage: `url(${leevoCover})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(88, 28, 135, 0.85)' }} />
        <div className="relative h-full flex flex-col justify-between p-12 xl:p-16">
          <div className="flex-1 flex flex-col justify-center">
            <div className="animate-fade-in-up animation-fill-backwards">
              <h2
                className="text-4xl xl:text-6xl 2xl:text-7xl font-bold leading-tight text-balance"
                style={{ color: '#ffffff' }}
              >
                The smarter way to run your sports program
              </h2>
              <p
                className="mt-6 text-lg xl:text-xl leading-relaxed text-pretty"
                style={{ color: 'rgba(255, 255, 255, 0.9)' }}
              >
                Leevo helps coaches and program directors manage classes, track student progress, and delight families
                with a seamless experience.
              </p>
            </div>

            <ul className="mt-8 space-y-3">
              {features.map((feature, index) => (
                <li
                  key={feature}
                  className={cn(
                    'flex items-center gap-3 text-lg animate-fade-in-up animation-fill-backwards',
                    index === 0 && 'animation-delay-100',
                    index === 1 && 'animation-delay-200',
                    index === 2 && 'animation-delay-300'
                  )}
                  style={{ color: '#ffffff' }}
                >
                  <span className="flex-shrink-0 size-2 rounded-full" style={{ backgroundColor: '#ffffff' }} />
                  {feature}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex gap-8 xl:gap-12">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={cn(
                    'animate-fade-in-up animation-fill-backwards',
                    index === 0 && 'animation-delay-200',
                    index === 1 && 'animation-delay-300',
                    index === 2 && 'animation-delay-400'
                  )}
                >
                  <p className="text-3xl xl:text-4xl font-bold tabular-nums" style={{ color: '#ffffff' }}>
                    {stat.value}
                  </p>
                  <p className="text-sm mt-1" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            className="mt-8 rounded-xl p-6 animate-fade-in-up animation-fill-backwards animation-delay-400"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
          >
            <p className="text-lg italic leading-relaxed" style={{ color: '#ffffff' }}>
              &ldquo;Champions are made when no one is watching, but they&apos;re built in programs that care.&rdquo;
            </p>
            <p className="mt-3 text-sm font-medium" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              — The Leevo Team
            </p>
          </div>
        </div>
      </div>

      <div className="flex min-h-dvh flex-col bg-white">
        <div className="flex flex-1 flex-col justify-center px-6 py-12 sm:px-12 lg:px-16 xl:px-24">
          <div className="mx-auto w-full max-w-md">
            <div className="text-center animate-fade-in-up animation-fill-backwards">
              <span className="text-3xl font-bold text-purple-700">Leevo</span>
            </div>

            <div className="mt-8 text-center animate-fade-in-up animation-fill-backwards animation-delay-100">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">Welcome back to Leevo</h1>
              <p className="mt-2 text-gray-600">Sign in to access your account</p>
            </div>

            {notificationMessage && (
              <div className="mt-6 rounded-md bg-green-50 border border-green-200 p-4 animate-fade-in-up">
                <p className="text-sm text-green-800">{notificationMessage}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5">
              {loginError && (
                <div
                  data-cy="error-message"
                  className="rounded-md bg-red-50 border border-red-200 p-4 animate-fade-in-up"
                >
                  <p className="text-sm text-red-800">{loginError}</p>
                  <p className="mt-2 text-sm text-red-700">
                    <a
                      data-cy="forgot-password-in-error"
                      href="/forgot_password"
                      className="font-medium underline hover:text-red-900"
                    >
                      Forgot your password? Reset it here.
                    </a>
                  </p>
                </div>
              )}

              <div className="space-y-2 animate-fade-in-up animation-fill-backwards animation-delay-200">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="Enter your email"
                  disabled={isSubmitting}
                  className={cn('focus-visible:ring-purple-500', errors.email && 'border-red-500')}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address'
                    }
                  })}
                />
                {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
              </div>

              <div className="space-y-2 animate-fade-in-up animation-fill-backwards animation-delay-300">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    disabled={isSubmitting}
                    className={cn(
                      'focus-visible:ring-purple-500 pr-10',
                      errors.password && 'border-red-500'
                    )}
                    {...register('password', {
                      required: 'Password is required'
                    })}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSubmitting}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:hover:text-gray-400"
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-sm text-red-600">{errors.password.message}</p>}
              </div>

              <div className="animate-fade-in-up animation-fill-backwards animation-delay-400">
                <Button
                  type="submit"
                  data-cy="submit"
                  disabled={isSubmitting}
                  className="w-full bg-purple-700 hover:bg-purple-800 focus-visible:ring-purple-500"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </div>

              <div className="text-center animate-fade-in-up animation-fill-backwards animation-delay-400">
                <a
                  data-cy="forgot-password"
                  href="/forgot_password"
                  className="text-sm font-medium text-purple-600 hover:text-purple-500"
                >
                  Forgot your password?
                </a>
              </div>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200 animate-fade-in-up animation-fill-backwards animation-delay-500">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-3">Run a sports program?</p>
                <a
                  href="/onboarding"
                  className="inline-flex items-center justify-center w-full px-4 py-2.5 border-2 border-purple-600 text-purple-700 font-medium rounded-md hover:bg-purple-50 transition-colors"
                >
                  Start Your Free Trial
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="block px-6 pb-8 lg:hidden">
          <div className="rounded-xl bg-purple-900 p-6">
            <h3 className="text-lg font-semibold text-white">The smarter way to run your sports program</h3>
            <div className="mt-4 flex justify-between">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-2xl font-bold text-white tabular-nums">{stat.value}</p>
                  <p className="text-xs text-purple-200">{stat.label}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm italic text-purple-200">
              &ldquo;Champions are made when no one is watching, but they&apos;re built in programs that care.&rdquo;
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

LoginPage.propTypes = {
  notificationMessage: PropTypes.string
}

export default LoginPage
